import React, { useState } from 'react';
import './ContactPage.css';

const ContactPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('/backend/api/contact.php', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setSubmitMessage('✅ Message sent! I\'ll get back to you within 24 hours.');
        form.reset();
      } else {
        setSubmitMessage('❌ Error: ' + (data.error || 'Failed to send message. Please try calling or texting instead.'));
      }
    } catch (error) {
      setSubmitMessage('❌ Network error. Please try calling or texting: 530-788-5017');
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <h1>Get In Touch</h1>
          <p className="subtitle">🚨 OMAHA METRO ONLY — Limited Spots Available</p>
        </div>
      </section>

      <section className="contact-content">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>Book Your Training</h2>
              <p className="contact-intro">
                Ready to take your game to the next level? Reach out today to schedule your first session. 
                Spots are filling fast, so don't wait!
              </p>

              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">📞</div>
                  <h3>Call or Text</h3>
                  <a href="tel:530-788-5017" className="contact-link">530-788-5017</a>
                  <p>Available for calls and texts</p>
                </div>

                <div className="contact-method">
                  <div className="method-icon">✉️</div>
                  <h3>Direct Message</h3>
                  <a href="https://x.com/CoachMesa" target="_blank" rel="noopener noreferrer" className="contact-link">@CoachMesa on X</a>
                  <p>DM me for fastest response</p>
                </div>
              </div>

              <div className="location-info">
                <h3>📍 Service Area</h3>
                <p className="location-text">Omaha Metro Area Only</p>
                <p className="location-note">Training locations arranged based on your needs</p>
              </div>

              <div className="scholarship-reminder">
                <h3>🎓 Scholarships Available</h3>
                <p>Low-income scholarships available for qualified athletes. Don't let cost be a barrier — reach out to discuss options.</p>
              </div>
            </div>

            <div className="contact-form-section">
              <div className="form-card">
                <h2>Quick Contact</h2>
                <p>Prefer to send a message? Fill out the form below and I'll get back to you ASAP.</p>
                
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input type="text" id="name" name="name" required placeholder="Your full name" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone *</label>
                    <input type="tel" id="phone" name="phone" required placeholder="(555) 123-4567" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input type="email" id="email" name="email" required placeholder="your.email@example.com" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="grade">Grade Level *</label>
                    <select id="grade" name="grade" required>
                      <option value="">Select grade</option>
                      <option value="8th">8th Grade</option>
                      <option value="9th">9th Grade (Freshman)</option>
                      <option value="10th">10th Grade (Sophomore)</option>
                      <option value="11th">11th Grade (Junior)</option>
                      <option value="12th">12th Grade (Senior)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="position">Position *</label>
                    <select id="position" name="position" required>
                      <option value="">Select position</option>
                      <option value="OL">Offensive Line</option>
                      <option value="DL">Defensive Line</option>
                      <option value="TE">Tight End</option>
                      <option value="RB">Running Back</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea id="message" name="message" rows={5} placeholder="Tell me about your goals, experience level, and what you're looking for..."></textarea>
                  </div>

                  {/* Honeypot field - hidden from users, catches bots */}
                  <input type="text" name="website" style={{display: 'none'}} tabIndex={-1} autoComplete="off" />

                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" name="scholarship" />
                      <span>I'm interested in scholarship opportunities</span>
                    </label>
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                  {submitMessage && (
                    <div className={`submit-message ${submitMessage.startsWith('✅') ? 'success' : 'error'}`}>
                      {submitMessage}
                    </div>
                  )}
                </form>

                <p className="form-note">* Required fields. I'll respond within 24 hours.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2025 Xcelerate Private Coaching. All rights reserved.</p>
          <p>Coach Mesa | Omaha Metro Area</p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;

